import React, { useCallback, useState, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import messages from './messages';
import { getStoreComponent } from './store-components';
import style from './style.module.less';
import { ResumeSsrActivationPath } from '../../../../../constants/cookies';
import { EnhancedReturnsStoreStatus } from '../../../../../enums/enhanced-returns-store-status';
import {
  HasExistingReturnRabbitAccount,
  StoreAlreadyRegisteredEnhancedReturns,
} from '../../../../../errors/user-friendly-error-message-service/code-constants';
import { CodeToFormattedMessageProps } from '../../../../../errors/user-friendly-error-message-service/code-to-formatted-message-props';
import { FeatureFlagDeclarations } from '../../../../../feature-flags/feature-flag-declarations';
import { useFeatureFlag } from '../../../../../feature-flags/hooks/use-feature-flag';
import { useMarketplaceApiCodeFromStoreGuid } from '../../../../../hooks/use-marketplace-api-code-from-store-guid';
import MarketplaceSetupModal from '../../../../../pages/settings/stores/marketplace-connection-modal/setup';
import { CookieService } from '../../../../../services/cookie-service';
import Global from '../../../../../store/global-selectors';
import { generateNativeStoreAuthenticationUrl, updateEnhancedReturnsStoreStatus } from '../../../../../store/returns/returns.api';
import { isMarketplaceAuthenticationInFlight } from '../../../../../store/returns/returns.helpers';
import { refreshStore, refreshStoreList } from '../../../../../store/seller/seller.actions';
import { Workflows } from '../../../../../tracking/tracking-constants';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { ActivationModalContent, ActivationModalFooter } from '../modal/components';
import ConnectingLoader from '../modal/components/connecting-loader';

const STORE_ACTIVATION_TIMEOUT = 10000; // 10 seconds
const ONE_DAY = 1;

const getPossibleFriendlyError = (axiosError) => {
  const data = axiosError?.response?.data || {};
  const { Errors = [] } = data;
  const error = Errors[0] || {};
  const mappedErrorCodes = [HasExistingReturnRabbitAccount, StoreAlreadyRegisteredEnhancedReturns];

  if (mappedErrorCodes.includes(error.Code)) {
    const errorMessageDescriptor = CodeToFormattedMessageProps(error.Code, error.ErrorContext, error.Message);
    return <FormattedMessage {...errorMessageDescriptor} />;
  }
};

const AuthenticateWithMarketplace = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentStoreStatus = location?.state?.storeActivationStatus;
  const { storeGuid } = useParams();
  const store = useSelector((state) => Global.selectSellerStoreData(state, { storeGuid }));
  const [lastStatusChangeTime, setLastStatusChangeTime] = useState(Date.now());
  const [retryCount, setRetryCount] = useState(0);
  const [retryFailed, setRetryFailed] = useState(false);
  const [showReauthModal, setShowReauthModal] = useState(false);
  const previousStatusRef = useRef(currentStoreStatus);
  const isStoreUpdating = useSelector(Global.selectSellerIsLoading);
  const enableSsrActivationNoAuth = useFeatureFlag(FeatureFlagDeclarations.enableSsrActivationNoAuth);
  const storeNeedsReauth = store?.needsReauthForSSR;

  // TODO: useMonoAuthCredentials is a bit of a misnomer.
  // If we're not using the SSR auth path then we ALWAYS use monoauth creds.
  // It's just a matter of whether we need to interrupt the user with a reauth via ConnectionsUI first before handing over monoauth creds to SSR
  const useMonoAuthCredentials = enableSsrActivationNoAuth && storeNeedsReauth === false;

  const authRedirectPath = location.pathname;
  const marketplaceApiCode = useMarketplaceApiCodeFromStoreGuid(storeGuid);

  const { mutate: updateStoreStatus } = useMutation({
    mutationFn: (guid) => updateEnhancedReturnsStoreStatus(guid),
  });

  const {
    isPending: isAuthenticating,
    isError: hasGenerateNativeStoreAuthenticationUrlError,
    mutate: generateNativeStoreAuthenticationUrlMutation,
    error: generateNativeStoreAuthenticationUrlAxiosError,
  } = useMutation({
    mutationFn: ({ storeGuid: guid, redirectUrl, useMonoAuthCredentials: useMonoCreds }) =>
      generateNativeStoreAuthenticationUrl(guid, redirectUrl, useMonoCreds),
  });

  const errorMessage = getPossibleFriendlyError(generateNativeStoreAuthenticationUrlAxiosError) || (
    <FormattedMessage {...messages.authenticationError} />
  );

  const isLoading = isMarketplaceAuthenticationInFlight(currentStoreStatus) || isAuthenticating || isStoreUpdating;
  const isError = hasGenerateNativeStoreAuthenticationUrlError || retryFailed;
  const trackActivateEnhancedReturns = useTrackWorkflow(Workflows.ACTIVATE_ENHANCED_RETURNS_FLOW);

  const onMarketplaceSetupBack = useCallback(() => {
    setShowReauthModal(false);
  }, []);

  const onCloseSetupModal = useCallback(() => {
    setShowReauthModal(false);
  }, []);

  const onSetupError = (error) => {
    trackActivateEnhancedReturns(WorkflowSteps.ERROR, {
      error,
    });
    setShowReauthModal(false);
  };

  const onRedirectToMarketplaceSetup = useCallback(() => {
    CookieService.setCookieValueWithExpiration(ResumeSsrActivationPath, authRedirectPath, ONE_DAY);
  }, [authRedirectPath]);

  // If we've been redirected back to this activation modal, we want to remove the cookie
  // that was set to resume the SSR activation process.
  useEffect(() => {
    CookieService.removeCookie(ResumeSsrActivationPath);
  }, []);

  /**
   * Since it's possible to wait indefinitely for the store activation to complete
   * due to webhooks received from SSR, we may find outselves in a situation
   * where we will want to call out to SSR to see whether or not they have created
   * the store on their end, and what state the store is in.
   *
   * If it's been more than 60 seconds since the last status change update was received,
   * we will ask SSR for the latest store data.
   *
   * If the store doesn't exist on the SSR side, we will assume that
   * store ouath either never completed/user denied permissions, and Shipstation will
   * set the store's status to Inactive.
   *
   * If the store exists on the SSR side, the status will be updated
   * to the latest status and we will refresh the store list in the UI.
   *
   * We will also set a retry count to limit the number of retries.
   */
  useEffect(() => {
    // If the store's activation status has changed, update the last change time
    if (currentStoreStatus !== previousStatusRef.current) {
      previousStatusRef.current = currentStoreStatus;
      setLastStatusChangeTime(Date.now());
      // Reset retry count when status changes
      setRetryCount(0);
    }

    // Do not set a timeout if not activating or retry limit is exceeded
    if (!isMarketplaceAuthenticationInFlight(currentStoreStatus) || retryCount >= 3) {
      // If we've reached the retry limit, set retryFailed to true
      if (retryCount >= 3) setRetryFailed(true);
      return undefined;
    }

    // set a timeout to refresh store data from SSR
    const timeoutId = setTimeout(() => {
      if (storeGuid) {
        // Increment retry count
        setRetryCount((prevCount) => prevCount + 1);
        updateStoreStatus(storeGuid, {
          // reload stores after retry
          onSuccess: (data) => {
            if (data?.enhancedReturnsActivationStatus === EnhancedReturnsStoreStatus.Inactive) {
              setRetryFailed(true);
            }
            dispatch(refreshStoreList());
          },
        });
      }
    }, STORE_ACTIVATION_TIMEOUT);

    return () => clearTimeout(timeoutId);
  }, [currentStoreStatus, lastStatusChangeTime, updateStoreStatus, storeGuid, dispatch, retryCount]);

  // Get the appropriate component based on the store's marketplaceId
  const StoreAuthComponent = ({ marketplaceId }) => {
    const Component = getStoreComponent(marketplaceId);
    return <Component store={store} />;
  };

  const displayReauthModal = useCallback(() => {
    setShowReauthModal(true);
  }, []);

  const loadNativeAuthenticationUrl = useCallback(() => {
    trackActivateEnhancedReturns(WorkflowSteps.REAUTHENTICATE_STORE, {
      marketplaceApiCode,
      storeGuid,
    });

    setRetryFailed(false);
    generateNativeStoreAuthenticationUrlMutation(
      {
        storeGuid,
        redirectUrl: window.location.href,
        useMonoAuthCredentials,
      },
      {
        onSuccess: (res) => {
          // If ecomRedirectUrl is empty but we have a success status, it's likely:
          // 1. the store has been authenticated and we already have the credentials
          // 2. the store is already registered and being re-activated
          // In either case, we can proceed to refresh the store data and skip presenting
          // the store authentication modal again.
          if (useMonoAuthCredentials || !res?.ecomRedirectUrl) {
            dispatch(refreshStore(storeGuid));
          } else if (res?.ecomRedirectUrl) {
            // Redirect to the marketplace authentication URL if available
            window.location.replace(res.ecomRedirectUrl);
          }
        },
      },
    );
  }, [
    dispatch,
    generateNativeStoreAuthenticationUrlMutation,
    marketplaceApiCode,
    storeGuid,
    trackActivateEnhancedReturns,
    useMonoAuthCredentials,
  ]);

  /**
   * TODO: we no longer have the featureFlag = false path now with this useEffect and onClickPrimaryButton=displayReauthModal
   * ...but it probably won't matter soon since this flag is now at 100%
   *
   * If we already have authentication credentials for the store, we don't have to present
   * the option for the user to re-authenticate. We will instead begin the process of activating
   * the store with SSR immediately.
   */
  useEffect(() => {
    if (useMonoAuthCredentials && isStoreUpdating === false) {
      loadNativeAuthenticationUrl();
    }
  }, [loadNativeAuthenticationUrl, useMonoAuthCredentials, isStoreUpdating]);

  return (
    <>
      <ActivationModalContent className={style.modalContent}>
        {isLoading ? (
          <ConnectingLoader
            marketplaceId={store?.marketplaceId}
            onClick={generateNativeStoreAuthenticationUrl}
            useMonoAuthCredentials={useMonoAuthCredentials}
          />
        ) : null}

        {!isLoading && isError ? (
          <BannerMessageV2
            content={<Text textType={TextType.Paragraph} value={errorMessage} />}
            layout={BannerLayout.Full}
            status={BannerStatus.Error}
          />
        ) : null}

        {!isLoading && <StoreAuthComponent marketplaceId={store?.marketplaceId} />}

        {showReauthModal ? (
          <MarketplaceSetupModal
            marketplaceApiCode={marketplaceApiCode}
            onBack={onMarketplaceSetupBack}
            onClose={onCloseSetupModal}
            onError={onSetupError}
            onRedirect={onRedirectToMarketplaceSetup}
            onSave={noop}
            storeGuid={storeGuid}
          />
        ) : null}
      </ActivationModalContent>

      <ActivationModalFooter
        disabled={isLoading}
        loading={isLoading}
        onClickPrimaryButton={() => displayReauthModal()}
        primaryButtonIcon
        primaryText={messages.reauthenticateButton}
      />
    </>
  );
};

export default AuthenticateWithMarketplace;
